import { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Paper,
    useTheme,
    IconButton
} from '@mui/material';
import { useParams, useNavigate } from 'react-router-dom';
import { useSignOut } from 'react-auth-kit';
import Axios from 'axios';
import Header from '../../components/Header';
import Loading from '../../components/Loading';
import { tokens } from '../../theme';
import { getAuthHeader } from '../../utils/authToken';
import Alert from '../../components/alert/Alert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AICompetitionSaveSection from './AICompetitionSaveSection';
import DuplicateResolutionModal from './DuplicateResolutionModal';
import MoreInfoModal from '../competitions/MoreInfoModal';

const AICompetitionSavePage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const { nid } = useParams();
    const navigate = useNavigate();
    const signOut = useSignOut();

    const [loading, setLoading] = useState(true);
    const [newsletter, setNewsletter] = useState(null);

    // AI Competition Save State
    const [aiCompetitionsToSave, setAiCompetitionsToSave] = useState([]);
    const [duplicateResolutionOpen, setDuplicateResolutionOpen] = useState(false);
    const [currentDuplicateCheck, setCurrentDuplicateCheck] = useState(null);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [resolvedDuplicates, setResolvedDuplicates] = useState({});

    // Preview Modal State
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [selectedCompetitionForPreview, setSelectedCompetitionForPreview] = useState(null);
    const [openEvents, setOpenEvents] = useState([]);
    const [deadlineEvents, setDeadlineEvents] = useState([]);
    const [testEvents, setTestEvents] = useState([]);
    const [winnerEvents, setWinnerEvents] = useState([]);
    const [competitionUpdated, setCompetitionUpdated] = useState(false);

    // Alerts
    const [successAlertOpen, setSuccessAlertOpen] = useState(false);
    const [successAlertMessage, setSuccessAlertMessage] = useState('');
    const [errorAlertOpen, setErrorAlertOpen] = useState(false);
    const [errorAlertMessage, setErrorAlertMessage] = useState('');

    const handleLogout = () => {
        signOut();
        navigate('/signin');
    };

    const fetchNewsletter = async () => {
        try {
            const response = await Axios.get(
                `${process.env.REACT_APP_URL}/api/v1/newsletters/${nid}`,
                { headers: { Authorization: getAuthHeader() } }
            );
            setNewsletter(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Newsletter fetch error:', error);
            if (error.response?.status === 401) {
                handleLogout();
            } else {
                setErrorAlertMessage(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    (error.request ? 'Network error. Please check your connection.' : error.message) ||
                    'Failed to load newsletter'
                );
                setErrorAlertOpen(true);
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        fetchNewsletter();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [nid]);

    const handleDuplicateResolution = async (resolution) => {
        const compIndex = resolution.ai_competition.original_index;

        if (resolution.action === 'skip') {
            // Track skip immediately (no API call)
            setResolvedDuplicates(prev => ({
                ...prev,
                [compIndex]: {
                    resolved: true,
                    action: 'skip',
                    existing_id: resolution.existing_id,
                    timestamp: Date.now()
                }
            }));
            setSuccessAlertMessage('Duplicate skipped');
            setSuccessAlertOpen(true);
        } else if (resolution.action === 'merge') {
            try {
                await Axios.put(
                    `${process.env.REACT_APP_URL}/api/v1/competitions/${resolution.existing_id}/merge`,
                    {
                        new_data: resolution.ai_competition.competition_details,
                        merge_fields: resolution.selected_fields
                    },
                    { headers: { Authorization: getAuthHeader() } }
                );

                // Track merge only after successful API call
                setResolvedDuplicates(prev => ({
                    ...prev,
                    [compIndex]: {
                        resolved: true,
                        action: 'merge',
                        existing_id: resolution.existing_id,
                        timestamp: Date.now()
                    }
                }));

                setSuccessAlertMessage('Competition merged successfully');
                setSuccessAlertOpen(true);
            } catch (error) {
                console.error('Merge error:', error);
                if (error.response?.status === 401) {
                    handleLogout();
                    return;
                }
                setErrorAlertMessage(
                    error.response?.data?.error ||
                    error.response?.data?.message ||
                    (error.request ? 'Network error. Please check your connection.' : error.message) ||
                    'Failed to merge competition'
                );
                setErrorAlertOpen(true);
                // Don't track resolution on error - user can try again
            }
        } else if (resolution.action === 'create') {
            // Track create immediately (no API call, just queuing)
            setResolvedDuplicates(prev => ({
                ...prev,
                [compIndex]: {
                    resolved: true,
                    action: 'create',
                    timestamp: Date.now()
                }
            }));
            setAiCompetitionsToSave([
                ...aiCompetitionsToSave,
                {
                    competition_index: resolution.ai_competition.original_index,
                    merge_strategy: 'create',
                    selected_fields: []
                }
            ]);
            setSuccessAlertMessage('Competition will be saved as new entry');
            setSuccessAlertOpen(true);
        }

        setDuplicateResolutionOpen(false);
        setCurrentDuplicateCheck(null);
    };

    const handleResetDecision = (compIndex) => {
        // Remove from resolved duplicates
        setResolvedDuplicates(prev => {
            const updated = { ...prev };
            delete updated[compIndex];
            return updated;
        });

        // Remove from save queue if it was there
        setAiCompetitionsToSave(
            aiCompetitionsToSave.filter(
                item => item.competition_index !== compIndex
            )
        );
    };

    const handleCompetitionPreview = async (competition) => {
        // Preview the AI-generated competition data
        const aiCompDetails = competition.competition_details;

        if (!aiCompDetails) {
            setErrorAlertMessage('Competition details not available for preview.');
            setErrorAlertOpen(true);
            return;
        }

        // Transform AI competition data to match MoreInfoModal expected format
        const competitionForPreview = {
            id: null, // No ID since not saved yet
            name: aiCompDetails.name,
            url: aiCompDetails.url,
            subject: aiCompDetails.subject || [],
            competition_type: aiCompDetails.competition_type || 'competition',
            memo: aiCompDetails.memo || '',
            eligibility_memo: aiCompDetails.eligibility_memo || '',
            test_format_memo: aiCompDetails.test_format_memo || ''
        };

        setSelectedCompetitionForPreview(competitionForPreview);

        // Parse AI competition events into categorized arrays
        const events = aiCompDetails.events || [];
        const categorizedEvents = {
            open: [],
            deadline: [],
            test: [],
            winner: []
        };

        events.forEach(event => {
            // Determine event category based on name or fixed_event_name
            const eventName = event.name?.toLowerCase() || '';
            const fixedName = event.fixed_event_name?.toLowerCase() || '';

            if (fixedName === 'open' || eventName.includes('open') || eventName.includes('registration open')) {
                categorizedEvents.open.push(event);
            } else if (fixedName === 'deadline' || eventName.includes('deadline') || eventName.includes('submission') || eventName.includes('registration deadline')) {
                categorizedEvents.deadline.push(event);
            } else if (fixedName === 'test' || eventName.includes('test') || eventName.includes('exam') || eventName.includes('competition date')) {
                categorizedEvents.test.push(event);
            } else if (fixedName === 'winner_announcement' || eventName.includes('winner') || eventName.includes('result')) {
                categorizedEvents.winner.push(event);
            } else {
                // Default to test events if unsure
                categorizedEvents.test.push(event);
            }
        });

        setOpenEvents(categorizedEvents.open);
        setDeadlineEvents(categorizedEvents.deadline);
        setTestEvents(categorizedEvents.test);
        setWinnerEvents(categorizedEvents.winner);

        setPreviewModalOpen(true);
    };

    const handlePreviewModalClose = () => {
        setPreviewModalOpen(false);
        setSelectedCompetitionForPreview(null);
        setOpenEvents([]);
        setDeadlineEvents([]);
        setTestEvents([]);
        setWinnerEvents([]);
    };

    if (loading) {
        return <Loading />;
    }

    if (!newsletter) {
        return (
            <Box m="20px">
                <Header title="Newsletter Not Found" subtitle="" />
            </Box>
        );
    }

    const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
    ];

    return (
        <Box m="20px">
            {/* Alerts */}
            <Alert
                successAlertOpen={successAlertOpen}
                setSuccessAlertOpen={setSuccessAlertOpen}
                errorAlertOpen={errorAlertOpen}
                setErrorAlertOpen={setErrorAlertOpen}
                successMsg={successAlertMessage}
                errorMsg={errorAlertMessage}
            />

            {/* Header with Back Button */}
            <Box display="flex" alignItems="center" mb={2}>
                <IconButton onClick={() => navigate(`/admin/newsletters/${nid}`)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Header
                    title={`Save AI Competitions - ${monthNames[newsletter.month - 1]} ${newsletter.year}`}
                    subtitle="Review and save AI-recommended competitions to database"
                />
            </Box>

            {/* Main Content */}
            <Paper sx={{ p: 3, backgroundColor: colors.primary[600] }}>
                <AICompetitionSaveSection
                    newsletter={newsletter}
                    aiCompetitionsToSave={aiCompetitionsToSave}
                    setAiCompetitionsToSave={setAiCompetitionsToSave}
                    onDuplicateFound={(duplicateData) => {
                        setCurrentDuplicateCheck(duplicateData);
                        setDuplicateResolutionOpen(true);
                    }}
                    onCompetitionPreview={handleCompetitionPreview}
                    resolvedDuplicates={resolvedDuplicates}
                    setResolvedDuplicates={setResolvedDuplicates}
                    onResetDecision={handleResetDecision}
                    saveInProgress={saveInProgress}
                    setSaveInProgress={setSaveInProgress}
                    setSuccessAlertMessage={setSuccessAlertMessage}
                    setSuccessAlertOpen={setSuccessAlertOpen}
                    setErrorAlertMessage={setErrorAlertMessage}
                    setErrorAlertOpen={setErrorAlertOpen}
                    onSaved={fetchNewsletter}
                />
            </Paper>

            {/* Duplicate Resolution Modal */}
            <DuplicateResolutionModal
                open={duplicateResolutionOpen}
                onClose={() => setDuplicateResolutionOpen(false)}
                duplicateData={currentDuplicateCheck}
                onResolved={handleDuplicateResolution}
            />

            {/* Preview Modal */}
            {selectedCompetitionForPreview && (
                <MoreInfoModal
                    open={previewModalOpen}
                    handleClose={handlePreviewModalClose}
                    competition={selectedCompetitionForPreview}
                    setCompetitionUpdated={setCompetitionUpdated}
                    openEvents={openEvents}
                    deadlineEvents={deadlineEvents}
                    testEvents={testEvents}
                    winnerEvents={winnerEvents}
                    setSuccessAlertOpen={setSuccessAlertOpen}
                    setSuccessAlertMessage={setSuccessAlertMessage}
                    setErrorAlertOpen={setErrorAlertOpen}
                    setErrorAlertMessage={setErrorAlertMessage}
                />
            )}
        </Box>
    );
};

export default AICompetitionSavePage;
